import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { ContextSource } from "@bitacorapro/api-types";
import { EmptyState } from "../components/EmptyState";
import { QueryBoundary } from "../components/QueryBoundary";
import { SkelTable } from "../components/skeleton/SkelTable";
import {
  failureReasonLabel,
  kindLabel,
  RETRYABLE_REASONS,
  statusLabel,
} from "../features/context/contextUpload";
import { ApiError } from "../lib/api/error";
import {
  useActivateContextSource,
  useArchiveContextSource,
  useContextRules,
  useContextSource,
  useRecipe,
  useRetryContextExtraction,
} from "../lib/api/queries";
import { useSession } from "../lib/auth/useSession";
import { canManageContextSources } from "../lib/capabilities";
import { fmtDateTime } from "../lib/util";
import { ContextSourceStatusPill } from "./ContextoMarca";

// /contexto/fuentes/:id — one document (ADR-0173 fase 2, PR-6): its
// state with the time it entered it, the failure reason with a retry when
// the reason is transient, the review counts that gate «Activar», and the
// two writes an org-scoped role has here (activate, archive). The rule
// list itself and the edit drawer land in PR-7.

type Conflict = { itemCode: string; param: string; scope: string | null; ruleIds: string[] };

function activationError(err: unknown): { title: string; detail?: string } {
  if (!(err instanceof ApiError)) return { title: "No se pudo activar." };
  if (err.code === "CONTEXT_SOURCE_NOT_REVIEWED") {
    const d = (err.details ?? {}) as { inConflict?: number; needingReview?: number };
    return {
      title: "Faltan reglas por revisar.",
      detail: `${d.inConflict ?? 0} en conflicto, ${d.needingReview ?? 0} marcadas por el extractor. Resuélvelas en «Revisar reglas».`,
    };
  }
  if (err.code === "CONTEXT_SLOT_CONFLICT") {
    const conflicts = ((err.details ?? {}) as { conflicts?: Conflict[] }).conflicts ?? [];
    return {
      title: "Dos documentos fijan valores distintos para el mismo ítem.",
      detail: conflicts
        .map((c) => `ítem ${c.itemCode} · ${c.param}${c.scope ? ` (${c.scope})` : ""}`)
        .join("; "),
    };
  }
  if (err.code === "CONTEXT_FOUR_EYES_REQUIRED") {
    return { title: "Debe activarlo una persona distinta de quien lo subió." };
  }
  return { title: err.message };
}

export function ContextoFuenteDetalle() {
  const { id } = useParams<{ id: string }>();
  const session = useSession();
  const canManage = canManageContextSources(session?.organization.role);
  const { data: recipe } = useRecipe();
  const source = useContextSource(id);
  const rules = useContextRules(
    id,
    source.data?.status === "in_review" || source.data?.status === "active"
  );
  const retry = useRetryContextExtraction();
  const activate = useActivateContextSource();
  const archive = useArchiveContextSource();
  const [actionError, setActionError] = useState<{ title: string; detail?: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>, done: string) {
    setActionError(null);
    try {
      await action();
      setNotice(done);
    } catch (err) {
      setActionError(activationError(err));
    }
  }

  return (
    <div className="page">
      <QueryBoundary
        query={source}
        label="el documento"
        errorTitle="No pudimos cargar el documento."
        skeleton={<SkelTable rows={4} cols={2} headers={["", ""]} />}
      >
        {(s: ContextSource) => (
          <>
            <div className="page-header">
              <div className="page-header__lead">
                <div className="page-header__eyebrow">
                  <Link to="/contexto">Contexto del cliente</Link> · {kindLabel(recipe, s.kind)}
                </div>
                <h1 className="page-header__title">{s.fileName ?? kindLabel(recipe, s.kind)}</h1>
                <p className="page-header__sub">
                  <ContextSourceStatusPill source={s} /> · subido el {fmtDateTime(s.createdAt)}
                </p>
              </div>
              {canManage && (
                <div className="page-header__actions">
                  {s.status === "failed" &&
                    s.failureReason &&
                    RETRYABLE_REASONS.has(s.failureReason) && (
                      <button
                        type="button"
                        className="btn btn--secondary"
                        disabled={retry.isPending}
                        onClick={() =>
                          void run(() => retry.mutateAsync(s.id), "Extracción reintentada.")
                        }
                      >
                        Reintentar extracción
                      </button>
                    )}
                  {s.status === "in_review" && (
                    <button
                      type="button"
                      className="btn btn--primary"
                      disabled={activate.isPending}
                      onClick={() =>
                        void run(
                          () => activate.mutateAsync({ id: s.id }),
                          "Versión activada. Las actas nuevas usan estas reglas."
                        )
                      }
                    >
                      Activar versión
                    </button>
                  )}
                  {s.status !== "archived" && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      disabled={archive.isPending}
                      onClick={() =>
                        void run(() => archive.mutateAsync(s.id), "Documento archivado.")
                      }
                    >
                      Archivar
                    </button>
                  )}
                </div>
              )}
            </div>

            {actionError && (
              <div className="empty" role="alert">
                <div className="empty__title">{actionError.title}</div>
                {actionError.detail && <div className="empty__sub">{actionError.detail}</div>}
              </div>
            )}
            {notice && (
              <div className="toast-zone">
                <div className="toast toast--ok" role="status" aria-live="polite">
                  {notice}
                </div>
              </div>
            )}

            <div className="stack-gap">
              <div className="panel">
                <div className="panel__head">
                  <div className="panel__title">Estado</div>
                </div>
                <div className="panel__body panel__body--info-list">
                  <Row k="Estado" v={statusLabel(s.status)} />
                  {s.status === "extracting" && s.extractionStartedAt && (
                    <Row k="Extrayendo desde" v={fmtDateTime(s.extractionStartedAt)} />
                  )}
                  {s.status === "failed" && (
                    <Row k="Motivo" v={failureReasonLabel(s.failureReason)} />
                  )}
                  {s.activatedAt && <Row k="Activado" v={fmtDateTime(s.activatedAt)} />}
                  {s.archivedAt && <Row k="Archivado" v={fmtDateTime(s.archivedAt)} />}
                  <Row
                    k="Vigencia"
                    v={
                      s.validFrom || s.validTo
                        ? `${s.validFrom ? fmtDateTime(s.validFrom) : "…"} – ${s.validTo ? fmtDateTime(s.validTo) : "…"}`
                        : "Hasta ser reemplazado"
                    }
                  />
                  <Row k="Tamaño" v={`${Math.max(1, Math.round(s.sizeBytes / 1024))} KB`} />
                  <Row k="Huella (sha256)" v={s.sha256.slice(0, 16)} mono />
                </div>
              </div>

              {(s.status === "in_review" || s.status === "active") && (
                <div className="panel">
                  <div className="panel__head">
                    <div className="panel__title">Reglas extraídas</div>
                  </div>
                  <QueryBoundary
                    query={rules}
                    label="las reglas"
                    errorTitle="No pudimos cargar las reglas."
                    kind="section"
                    skeleton={<SkelTable rows={2} cols={2} headers={["", ""]} />}
                  >
                    {(r) => (
                      <div className="panel__body panel__body--info-list">
                        <Row k="Total" v={String(r.counts.total)} />
                        <Row k="En conflicto" v={String(r.counts.inConflict)} />
                        <Row k="Marcadas por el extractor" v={String(r.counts.needingReview)} />
                        <Row k="Descartadas" v={String(r.counts.discarded)} />
                        <Row k="Sin ítem asociado" v={String(r.counts.withoutItems)} />
                        <p className="cell-meta">
                          {r.counts.inConflict === 0 && r.counts.needingReview === 0
                            ? "Lista para activar. "
                            : "Resuelve los conflictos y revisa las marcadas antes de activar. "}
                          <Link to={`/contexto/fuentes/${s.id}/reglas`}>Revisar reglas</Link>
                        </p>
                      </div>
                    )}
                  </QueryBoundary>
                </div>
              )}

              {s.status === "uploaded" && (
                <EmptyState
                  title="En cola para extracción."
                  description="El servicio de IA tomará el documento en segundos. Recarga para ver el avance."
                />
              )}
            </div>
          </>
        )}
      </QueryBoundary>
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="info-row">
      <span className="info-row__k">{k}</span>
      <span className={mono ? "info-row__v info-row__v--mono" : "info-row__v"}>{v}</span>
    </div>
  );
}
